import { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  Image,
  Modal,
  Container,
  Breadcrumb,
  Alert,
  Badge,
} from "react-bootstrap";
import {  MEDIA_BASE_URL } from "@/api/api";
import { useDispatch, useSelector } from "react-redux";
import { taskDetail, approveTask, rejectTask } from "@/store/tasksApi/tasksThunk";
import formatDate from "../utils/formatDate";
import toast from "react-hot-toast";
import { MdOutlineDateRange, MdOutlineUpdate } from "react-icons/md";
import { FaDownload, FaCheckCircle, FaTimesCircle, FaThumbsUp, FaTimes, FaCheck, FaImage } from "react-icons/fa";
import { Timeline, Statistic } from "antd"; // Ant Design Timeline
import { HomeOutlined } from "@ant-design/icons";
import { getFileName, normalizeUrl } from "../utils/normalizeUrl";

const { Countdown } = Statistic;

const STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  SUBMITTED: "submitted",
  APPROVED: "approved",
  REJECTED: "rejected",
  PENDING_AFTER_REJECTION: "pending_after_rejection",
};
// STATUS xaritalari
const STATUS_LABEL = {
  pending: "Yaratildi",
  in_progress: "Bajarilmoqda",
  submitted: "Topshirildi",
  approved: "Tasdiqlandi",
  rejected: "Rad etildi",
  pending_after_rejection: "Rad etilgan",
};

const STATUS_COLOR = {
  pending: "gray",
  in_progress: "blue",
  submitted: "#faad14",
  approved: "green",
  pending_after_rejection: "red",
  rejected: "red",
};


// 25-09-25 14:35 format
const formatShortDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yy = String(d.getFullYear()).slice(-2);
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${dd}-${mm}-${yy} ${hh}:${mi}`;
};

const DAY = 24 * 60 * 60 * 1000;

const ApproveTaskDetail = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { tasksDetailData: task } = useSelector((state) => state.tasksApi);
  const isPending = task?.status === STATUS.PENDING;
  const isInProgress = task?.status === STATUS.IN_PROGRESS;
  const isTerminal = [STATUS.SUBMITTED, STATUS.APPROVED, STATUS.REJECTED].includes(task?.status);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [reason, setReason] = useState("");
  const [newDeadline, setNewDeadline] = useState("");

  useEffect(() => {
    if (id) dispatch(taskDetail(id));
  }, [dispatch, id]);

  // === Approve ===
  const handleApprove = () => {
    dispatch(approveTask(task.id)).then((res) => {
      if (res?.meta?.requestStatus === "fulfilled") {
        toast.success("Topshiriq tasdiqlandi");
        if (id) dispatch(taskDetail(id));
      } else {
        toast.error(res?.payload || "Tasdiqlashda xatolik");
      }
    });
  };

  // === Reject ===
  const handleReject = () => {
    if (!reason.trim()) {
      toast.error("Rad etish sababi kiritilishi shart");
      return;
    }

    const today = new Date();
    // Kunning boshiga normallash (00:00)
    today.setHours(0, 0, 0, 0);

    const currentDeadline = task?.deadline ? new Date(task.deadline) : null;
    if (currentDeadline) currentDeadline.setHours(0, 0, 0, 0);

    let finalDeadline = currentDeadline;

    if (newDeadline) {
      const chosenDeadline = new Date(newDeadline);
      chosenDeadline.setHours(0, 0, 0, 0);
      if (chosenDeadline < today) {
        toast.error("Deadline bugungi kundan kichik bo‘lishi mumkin emas");
        return;
      }
      finalDeadline = chosenDeadline;
    } else {
      if (currentDeadline && currentDeadline < today) {
        toast.error("Eski deadline muddati o‘tib ketgan, yangi deadline tanlang");
        return;
      }
    }

    dispatch(
      rejectTask({
        taskId: task.id,
        reason,
        deadline: finalDeadline ? finalDeadline.toISOString().split("T")[0] : undefined,
      })
    ).then((res) => {
      if (res?.meta?.requestStatus === "fulfilled") {
        toast.success("Topshiriq rad etildi");
        setShowRejectModal(false);
        setReason("");
        setNewDeadline("");
        if (id) dispatch(taskDetail(id));
      } else {
        toast.error(res?.payload || "Rad etishda xatolik yuz berdi");
      }
    });
  };

  // Timeline uchun tayyorlash
  const rawHistory = Array.isArray(task?.status_history) ? task.status_history : [];
  const lastStatus = rawHistory.length ? rawHistory[rawHistory.length - 1].status : null;

  // Birinchi elementni (odatda pending) ko‘rsatmaymiz
  const timelineHistory = rawHistory.slice(1);

  const pendingText = lastStatus === "approved" ? undefined : "Kutilmoqda";

  const timelineItems = timelineHistory.map((h) => {
    const title = `${STATUS_LABEL[h.status] || h.status}${h.reason ? " — " + h.reason : ""}`;
    return {
      color: STATUS_COLOR[h.status] || "gray",
      children: (
        <div>
          <div className="d-flex flex-column justify-content-between">
            <strong>{title}</strong>
            <small className="text-muted">{formatShortDateTime(h.timestamp)}</small>
          </div>
        </div>
      ),
    };
  });

  // === TIMER (deadline gacha) ===
  const deadlineTs = useMemo(() => (task?.deadline ? new Date(task.deadline).getTime() : null), [task?.deadline]);
  const remainingMs = useMemo(() => (deadlineTs ? deadlineTs - Date.now() : null), [deadlineTs]);

  const timerVariant = useMemo(() => {
    if (remainingMs == null) return "success";
    if (remainingMs <= 0) return "danger";
    if (remainingMs <= 1 * DAY) return "danger";
    if (remainingMs <= 3 * DAY) return "warning";
    return "success";
  }, [remainingMs]);

  const timerBoxClass =
    timerVariant === "danger"
      ? "bg-danger-subtle border border-danger text-danger"
      : timerVariant === "warning"
        ? "bg-warning-subtle border border-warning text-warning"
        : "bg-success-subtle border border-success text-success";


  const firstAttachmentImage = isTerminal
    ? (task?.attachments || []).find((a) => a.image_url)?.image_url
    : null;

  const formatUrl = (url) => {
    if (!url) return null;
    return url.replace("/apis", ""); // faqat birinchi uchragan /apis ni olib tashlaydi
  };

  const heroImageSrc = firstAttachmentImage
    ? (firstAttachmentImage.startsWith("http")
      ? formatUrl(firstAttachmentImage)
      : MEDIA_BASE_URL + firstAttachmentImage
    )
    : (task?.image ? formatUrl(MEDIA_BASE_URL + task.image) : null);

  



  return (
    <Container fluid>
      <Breadcrumb className="py-2">
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/tasks/list" }}>
          Topshiriqlar
        </Breadcrumb.Item>
        <Breadcrumb.Item active>{task?.title}</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="justify-content-center">
        <Col>
          <Card className="border-0 rounded-3">
            <CardBody>
              {/* ===== TOP TIMER ===== */}
              {deadlineTs && (isPending || isInProgress) && (
                <div className={`p-3 rounded-3 d-flex flex-wrap align-items-center justify-content-between mb-3 ${timerBoxClass}`}>
                  <div className="me-3">
                    <div className="fw-semibold">Deadline gacha</div>
                    <small className="opacity-75"> {formatShortDateTime(task?.deadline)} gacha</small>
                  </div>
                  {remainingMs > 0 ? (
                    <Countdown
                      value={deadlineTs}
                      format="DD [kun] HH:mm:ss"
                      valueStyle={{ fontWeight: 700, fontSize: 22 }}
                    />
                  ) : (
                    <div className="fw-bold" style={{ fontSize: 15 }}>Muddati tugagan</div>
                  )}
                </div>
              )}
              {/* ===== /TOP TIMER ===== */}


              {/* User ma'lumotlari */}
              <div className="d-flex align-items-center mb-4 p-3   rounded border-primary">
                <Image
                  src={task?.approver?.picture || "https://via.placeholder.com/60"}
                  roundedCircle
                  width={50}
                  height={50}
                  className="me-3 shadow-sm object-fit-cover"
                />
                <div>
                  <h6 className="mb-0 fw-bold">
                    {task?.approver?.first_name} {task?.approver?.surname}{" "}
                  </h6>
                  <small className="text-muted">Ijroni ta’minlash uchun mas’ul</small>
                </div>
              </div>

              {/* ==== HERO SECTION ==== */}
              <h4 className="mb-3 text-primary">{task?.title}</h4>

              <Row className="g-4 align-items-start mb-4">
                {/* Left: description */}
                <Col md={6}>
                  <div className="p-3 border rounded">
                    <h6 className="text-muted mb-2">Tavsif</h6>
                    <p className="mb-3">{task?.description || "—"}</p>
                  </div>
                </Col>

                {/* Right: image + meta */}
                <Col md={6}>
                  <div className="ratio ratio-16x9 border rounded overflow-hidden">
                    {heroImageSrc ? (
                      <Image
                        src={heroImageSrc}
                        alt="Task image"
                        className="w-100 h-100"
                        style={{ objectFit: "cover" }}
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-body-tertiary text-muted">
                        <FaImage className="me-2" /> Rasm mavjud emas
                      </div>
                    )}
                  </div>

                  {/* Root file (terminal bo'lmaganda) */}
                  {!isTerminal && task?.file && (
                    <div className="mt-3">
                      <a
                        className="btn btn-outline-secondary"
                        href={MEDIA_BASE_URL + task.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textTransform: "none" }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <FaDownload />
                          <span>Faylni ochish</span>
                        </div>
                      </a>
                    </div>
                  )}

                  <div className="small mt-3 d-flex flex-column gap-1">
                    <div className="mb-1"><strong>Yaratilgan:</strong> {formatDate(task?.created_at)}</div>
                    <div><strong>Muddati:</strong> {formatDate(task?.deadline)}</div>
                  </div>
                </Col>
              </Row>
              {/* ==== /HERO SECTION ==== */}

              {/* Terminal holatlarda attachments */}
              <div className="row">

                {/* Timeline */}
                {timelineItems.length > 0 && (
                  <div className="mb-4 col-lg-4">
                    <h5 className="mt-4 mb-3">Holatlar tarixi</h5>
                    <Timeline pending={pendingText} items={timelineItems} />
                  </div>
                )}

                {isTerminal && Boolean(task?.attachments?.length) && (
                  <div className="mb-4 col-lg-8">
                    <h5 className="mb-2">Biriktirilgan fayllar</h5>
                    <Row className="g-3">
                      {task.attachments.map((att) => (
                        <Col md={6} key={att.id}>
                          {att.image_url && (
                            <div className="mb-2">
                              <a
                                className="btn btn-outline-secondary"
                                href={MEDIA_BASE_URL + att.image_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textTransform: "none" }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <FaDownload />
                                  <span>{getFileName(att.image_url)}</span>
                                </div>
                              </a>
                            </div>
                          )}
                          {att.file_url && (
                            <a
                              className="btn btn-outline-secondary"
                              href={MEDIA_BASE_URL + att.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ textTransform: "none" }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <FaDownload />
                                <span>{getFileName(att.file_url)}</span>
                              </div>
                            </a>
                          )}
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
              </div>

              {/* Vazifalar — LIST */}
              <h5 className="mt-4 mb-3">Vazifalar</h5>
              {task?.steps?.length > 0 ? (
                <ul className="list-group mb-4">
                  {task.steps.map((step) => (
                    <li
                      key={step.id}
                      className="list-group-item d-flex align-items-center justify-content-between"
                    >
                      <div className="d-flex align-items-center gap-2">
                        {step.is_completed ? (
                          <FaCheckCircle className="text-success" />
                        ) : (
                          <FaTimesCircle className="text-danger" />
                        )}
                        <span className={step.is_completed ? "text-decoration-line" : ""}>
                          {step.title}
                        </span>
                      </div>
                      {step.is_completed ? (
                        <Badge bg="success">Bajarildi</Badge>
                      ) : (
                        <Badge bg="secondary">Bajarilmagan</Badge>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <Alert variant="secondary" className="text-center">
                  Hozircha step qo‘shilmagan
                </Alert>
              )}

              {/* Action buttons — faqat submitted holatda ko‘rinsin */}
              <div className="text-end">
                {task?.status === "submitted" && (
                  <>
                    <Button variant="success" className="me-2" onClick={handleApprove}>
                      Tasdiqlash <FaCheck className="ms-2" />
                    </Button>
                    <Button
                      style={{ textTransform: "none" }}
                      variant="danger"
                      onClick={() => setShowRejectModal(true)}
                    >
                      Rad etish <FaTimes className="ms-2" />
                    </Button>
                  </>
                )}
              </div>
              <div className="text-end">
                {task?.status === "approved" && (
                  <Alert
                    variant="success"
                    className="d-flex align-items-center gap-2 justify-content-center"
                  >
                    <FaThumbsUp className="ms-2" /> Topshiriq tasdiqlangan.
                  </Alert>
                )}
                {task?.status === "rejected" && (
                  <Alert
                    variant="danger"
                    className="d-flex align-items-center gap-2 justify-content-center"
                  >
                    <FaTimes className="ms-2" /> Topshiriq rad etilgan.
                  </Alert>
                )}
              </div>

            </CardBody>
          </Card>
        </Col>

        {/* Reject Modal */}
        <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Rad etish</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Rad etish sababi</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Yangi deadline (ixtiyoriy)</Form.Label>
                <Form.Control
                  type="date"
                  value={newDeadline}
                  onChange={(e) => setNewDeadline(e.target.value)}
                />
                <Form.Text className="text-danger">
                  Agar tanlamasangiz, eski deadline olinadi
                </Form.Text>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              style={{ textTransform: "none" }}
              className="d-flex align-items-center"
              variant="danger"
              onClick={handleReject}
            >
              <span>Rad etish</span> <FaTimes className="ms-2" />
            </Button>
          </Modal.Footer>
        </Modal>
      </Row>
    </Container>
  );
};

export default ApproveTaskDetail;
